/**
 * The location tree, assembled from the flat list the store hands back.
 *
 * The rolling-up happens here rather than in SQL on purpose: a recursive CTE
 * per request would be the Durable Object doing arithmetic that a hundred-node
 * tree does in microseconds on the client, and both the browse screen and the
 * picker want the whole tree anyway. It is pure, so it has a test rather than a
 * comment claiming it works.
 */

import type { LocationCount } from "./types.js";

export interface LocationNode extends LocationCount {
  children: LocationNode[];
  /** This location's items plus everything below it, which is what a shelf's count means. */
  totalCount: number;
  /** Root first: ["Basement", "Shelf A", "Bin 3"]. */
  path: string[];
}

/** The separator between the steps of a location's path, everywhere one is shown. */
export const PATH_SEPARATOR = " › ";

/**
 * A parent that is missing -- deleted, or belonging to nothing -- makes its
 * children roots rather than losing them. An orphan on the screen is a thing
 * somebody can fix; an orphan that is silently not on the screen is not.
 */
export function locationTree(locations: readonly LocationCount[]): LocationNode[] {
  const byId = new Map<string, LocationNode>();
  for (const location of locations) {
    byId.set(location.id, { ...location, children: [], totalCount: location.itemCount, path: [] });
  }

  const roots: LocationNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }

  // Depth-first, children before parents, so a parent's total is already the
  // sum of finished subtotals. A cycle (which no route can create, but a
  // hand-edited row could) is broken by the `seen` set rather than hanging.
  const seen = new Set<string>();
  const settle = (node: LocationNode, trail: string[]): number => {
    if (seen.has(node.id)) return 0;
    seen.add(node.id);
    node.path = [...trail, node.name];
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.totalCount = node.itemCount;
    for (const child of node.children) node.totalCount += settle(child, node.path);
    return node.totalCount;
  };

  roots.sort((a, b) => a.name.localeCompare(b.name));
  for (const root of roots) settle(root, []);
  return roots;
}

/** Every node of the tree, parents before children, for a picker's flat list. */
export function flattenLocations(nodes: readonly LocationNode[]): LocationNode[] {
  const out: LocationNode[] = [];
  const walk = (node: LocationNode) => {
    out.push(node);
    for (const child of node.children) walk(child);
  };
  for (const node of nodes) walk(node);
  return out;
}

/** "Basement › Shelf A › Bin 3", or "" for an item that is nowhere in particular. */
export const pathLabel = (path: readonly string[]): string => path.join(PATH_SEPARATOR);
